import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import { Icon } from '@/components/icon';
import { company } from '@/data/company';

export interface NavItem {
  to: string;
  label: string;
  icon: 'TreeDeciduous' | 'Hammer' | 'Axe' | 'Eye' | 'Award' | 'Mail';
}

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  items: NavItem[];
}

export function MobileNav({ isOpen, onClose, items }: MobileNavProps): React.ReactNode {
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          key="mobile-nav"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] bg-bark text-paper"
          style={{ backgroundColor: '#1c2218' }}
        >
          <motion.div
            initial={{ y: -16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -16, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.2, 0.7, 0.2, 1] }}
            className="mx-auto flex h-full max-w-6xl flex-col px-5 py-6 sm:px-8"
          >
            <div className="flex items-center justify-between">
              <span className="font-display text-sm uppercase tracking-[0.3em] text-paper/60">
                Menü
              </span>
              <button
                type="button"
                aria-label="Menü schließen"
                onClick={onClose}
                className="rounded-md p-2 hover:bg-moss"
              >
                <Icon name="X" className="h-6 w-6" />
              </button>
            </div>

            <nav className="mt-12 flex flex-col gap-1" aria-label="Mobile Navigation">
              {items.map((item, index) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + index * 0.05, duration: 0.4 }}
                >
                  <Link
                    to={item.to}
                    onClick={onClose}
                    className="group flex items-center gap-4 border-b border-paper/10 py-3 transition-colors hover:text-leaf"
                  >
                    <Icon
                      name={item.icon}
                      className="h-6 w-6 shrink-0 text-paper/50 transition-colors group-hover:text-leaf"
                    />
                    <span className="font-display text-3xl tracking-tight">{item.label}</span>
                  </Link>
                </motion.div>
              ))}
            </nav>

            <div className="mt-auto flex flex-col gap-3 pt-8">
              <a
                href={company.phone.href}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-paper px-4 py-3 font-medium text-bark hover:bg-stone"
              >
                <Icon name="Phone" className="h-5 w-5" />
                {company.phone.display}
              </a>
              <a
                href={company.whatsapp.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-paper/30 px-4 py-3 text-paper hover:bg-paper/10"
              >
                <Icon name="MessageCircle" className="h-5 w-5" />
                WhatsApp
              </a>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
